'use client';

import { useEffect, useState } from "react";
import {
  Avatar, Box, Button, CloseButton, Flex, HStack, Text, VStack,
} from "@chakra-ui/react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell, ChevronDown, ChevronUp, User, FileText, Repeat, ListChecks, FileArchive, LogIn, LogOut,
  UserPlus,
} from 'lucide-react';

// Hooks
import { isAuthenticated } from "middlewares/auth";
import { useAuth } from "../../../hooks/auth";
// Data

// Styles
import { HeaderComponentsStyles } from "styles/headerStyles";
import { getUserFirstName } from "utils/userUtils";
import { usePermission } from "hooks/userPermissionHooks";
import { menuItemsMobileData } from "data/siteData";
import { useUnreadNotifications } from "hooks/useUnreadNotifications";

const MotionBox = motion.create(Box);

const CounterBadge = ({ count }: { count: number }) => {
  if (count <= 0) return null;

  return (
    <Flex
      align="center"
      justify="center"
      minW="20px"
      h="20px"
      px="1.5"
      bg="red.600"
      color="white"
      borderRadius="full"
      fontSize="xs"
      fontWeight="bold"
      lineHeight="1"
    >
      {count > 99 ? "99+" : count}
    </Flex>
  );
};

const collapsibleMenus = [
  {
    label: "Permutas",
    icon: Repeat,
    dataKey: "menuPermutasMobileData",
    value: "permuta"
  },
  {
    label: "Classificados",
    icon: ListChecks,
    dataKey: "menuClassificadosMobileData",
  },
  {
    label: "Documentos",
    icon: FileText,
    dataKey: "menuDocumentosMobileData",
    value: "documento"
  },
  {
    label: "Banco de Peças",
    icon: FileArchive,
    dataKey: "menuPecasMobileData",
    value: "peca"
  },
  {
    label: "Minha Conta",
    icon: User,
    dataKey: "menuMinhaContaMobileData",
  },
];

export default function MobileRightSidebar({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {
  const { logout } = useAuth();
  const [userName, setUserName] = useState<string>("");
  const [userUsername, setUserUsername] = useState<string>("");
  const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [consultCode, setConsultCode] = useState<string | null>(null);
  const { unreadCount } = useUnreadNotifications();

  useEffect(() => {
    const checkAuthAndFetchName = async () => {
      const auth = isAuthenticated();
      setUserIsAuthenticated(auth);

      if (auth) {
        const nameFromStorage = localStorage.getItem("firstName");
        const usernameFromStorage = localStorage.getItem("username");
        const code = localStorage.getItem("consultCode");

        if (nameFromStorage) setUserName(nameFromStorage);
        if (usernameFromStorage) setUserUsername(usernameFromStorage);
        if (code) setConsultCode(code);

        if (!nameFromStorage) {
          try {
            const fetchedName = await getUserFirstName();
            if (fetchedName) setUserName(fetchedName);
          } catch (error) {
            console.error("Erro ao buscar nome do usuário:", error);
          }
        }
      }
    };

    checkAuthAndFetchName();
  }, []);

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    logout?.();
    onClose();
  };
  const { hasPermission } = usePermission();
  if (!isOpen) return null;

  return (
    <>
      <Box {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuBox}>
        <Flex {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuHeader}>
          <Text {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuHeaderText}>Menu</Text>
          <CloseButton onClick={onClose} size="lg" />
        </Flex>

        {/* User Info */}
        {userIsAuthenticated && (
          <Flex {...HeaderComponentsStyles.rightSidebarMobile.flexAvatar}>
            <Box position="relative" mt="2">
              <Avatar.Root
                size="2xl"
                _selected={{ border: "none" }}
                transition="all 0.2s ease-in-out"
                _hover={{ transform: "scale(1.08)", bg: "red.700", border: "none" }}
              >
                <Avatar.Fallback />
              </Avatar.Root>
              {unreadCount > 0 && (
                <Box position="absolute" top="-1" right="-1">
                  <CounterBadge count={unreadCount} />
                </Box>
              )}
            </Box>
            <Text color="gray.500" fontWeight="bold">{userName}</Text>
            {userUsername && (
              <Text color="gray.400" fontSize="sm">@{userUsername}</Text>
            )}
            <Box {...HeaderComponentsStyles.rightSidebarMobile.div} bg="gray.200" />

            {userIsAuthenticated && consultCode && (
              <Flex {...HeaderComponentsStyles.rightSidebarMobile.mainFlexConsultCode}>
                <Text color="gray.500" fontSize="sm">
                  Código Associado
                </Text>
                <Flex  {...HeaderComponentsStyles.rightSidebarMobile.flexConsultCode}>
                  <Text color="white" fontSize="sm">
                    {consultCode}
                  </Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        )}

        {/* Divider */}
        <Box {...HeaderComponentsStyles.rightSidebarMobile.div} bg="gray.300" />
        <VStack align="stretch" px={4} mt={2}>
          {userIsAuthenticated && (
            <>
              <Link href="/conta/menu/notificacoes" passHref legacyBehavior>
                <Button
                  as="a"
                  {...HeaderComponentsStyles.rightSidebarMobile.ItemButton}
                  onClick={onClose}
                >
                  <HStack gap="2" minW="0">
                    <Bell size={18} />
                    <Text>Notificações</Text>
                  </HStack>
                  <CounterBadge count={unreadCount} />
                </Button>
              </Link>

              {collapsibleMenus.map(({ label, icon: Icon, dataKey, value }) => {
                // Verifica se tem value e, se tiver, checa permissão
                if (value && !hasPermission(value, 1)) return null;

                const submenuItems = menuItemsMobileData[dataKey as keyof typeof menuItemsMobileData];
                const isOpenMenu = openMenus[dataKey];

                return (
                  <Box key={dataKey}>
                    <Button
                      onClick={() => toggleMenu(dataKey)}
                      {...HeaderComponentsStyles.rightSidebarMobile.ItemButton}
                    >
                      <HStack>
                        <Icon size={18} />
                        <Text>{label}</Text>
                      </HStack>
                      {isOpenMenu ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </Button>

                    <AnimatePresence>
                      {isOpenMenu && (
                        <MotionBox
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          overflow="hidden"
                          {...HeaderComponentsStyles.rightSidebarMobile.SubmenuBox}
                        >
                          {submenuItems.map((item) => (
                            <Link key={item.label} href={item.href} passHref legacyBehavior>
                              <Button
                                as="a"
                                {...HeaderComponentsStyles.rightSidebarMobile.SubmenuItem}
                                onClick={onClose}

                              >
                                <Text fontWeight="bold">{item.label}</Text>
                              </Button>
                            </Link>
                          ))}
                        </MotionBox>
                      )}
                    </AnimatePresence>
                  </Box>
                );
              })}
            </>
          )}

          {/* Associe-se ou Entrar/Sair */}
          <Box pt={6}>
            {!userIsAuthenticated && (
              <>
                <Link href="/conta/login" passHref legacyBehavior>
                  <Button
                    as="a"
                    {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuLoginButton}
                    onClick={onClose}
                  >
                    <LogIn size={18} />
                    Entrar
                  </Button>
                </Link>

                <Link href="/conta/cadastro" passHref legacyBehavior>
                  <Button
                    as="a"
                    {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuSignupButton}
                    onClick={onClose}
                  >
                    <UserPlus size={18} />
                    Associe-se
                  </Button>
                </Link>
              </>
            )}

            {userIsAuthenticated && (
              <Button
                onClick={handleLogout}
                {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuItemButton}
              >
                <HStack>
                  <LogOut size={18} />
                  <Text>Sair</Text>
                </HStack>
              </Button>
            )}
          </Box>
        </VStack>
      </Box>

      {/* Overlay */}
      <Box {...HeaderComponentsStyles.rightSidebarMobile.mobileMenuOverlay} onClick={onClose} />
    </>
  );
}
